import { t } from 'i18next'
import React from 'react'
import { any, async, fromJSX, loadingAsyncState, nothing, StandardLocalWidget, unloadedAsyncState, Updater } from 'widgets-for-react'
import { fieldUpdater } from '../../../../../../Shared/Frontend/utils/fieldUpdater'
import { getClassNameModern } from '../../../../../../Shared/Frontend/utils/getClassNameFromStyle'
import { Loader } from '../../../../../../Shared/Frontend/utils/loader'
import { State } from '../../../cmsPage.widget'
import type { DP } from '../../../drupal/blockDefinitions'
import { DonationHeaderLayout } from '../../../header/pageHeader/donationHeader.layout'
import { parseExpandable } from '../../../utils/expandable'
import type { ModernBlock, SeoEvent } from '../../modernBlocks.state'
import { ExpandableLayout } from '../expandableBlock/expandableBlock.layout'
import { createCustomStep3, donationFormRequest } from './donationFormBlock.api'
import { donationBlockClasses, DonationBlockLayout } from './donationFormBlock.layout'
import { DonationFormState, initStep2 } from './donationFormBlock.state'
import { step2AmountFormWidget } from './elements/step2Form'
import { step3PersonalFormWidget } from './elements/step3Form'

export const donationFormBlock: ModernBlock<DonationFormState, any, DP.DonationForm> = {
  kind: 'donationFormBlock',
  render: (setRootState, rootState, blockIndex, blockProps, amountNested) =>
    donationFormWidget(rootState, setRootState, blockIndex)(blockProps).map(u => s => u(s)),
  adapters: {
    drupal: (dpBlock: DP.DonationForm, blockIndex: number) => {
      const target = dpBlock.target.type[0].target_id
      if (target != 'donation_form') return undefined

      return {
        blockIndex: blockIndex,
        formPreset: dpBlock.target.field_donatie_[0].target_label,
        text: dpBlock.target.field_body[0]?.processed,
        step: '1-Unloaded',
        anchorTitle: `donation-${blockIndex}`,
        submitState: unloadedAsyncState(),
        loader: loadingAsyncState(() => donationFormRequest(dpBlock.target.field_donatie_[0].target_id)),
        title: dpBlock.target.field_title ? dpBlock.target.field_title[0]?.value : t("DonationBlock:DonationForm.Title"),
        expandableItems: parseExpandable(dpBlock.target.field_expandable_content_blocks),
      }
    }
  },
  sampleExample: [
  ],
  seoEvents: (props, blockIndex) =>
    [

    ].filter((i) => i != undefined) as SeoEvent[],
  inView: {
    showAs: 'section',
    threshold: 0.1,
    triggerOnce: true,
    inViewClassName: getClassNameModern(donationBlockClasses, 'inview', 'inview'),
    updateState: (currentInviewState, currentBlockState) => {
      return {
        ...currentBlockState,
        inviewState: currentInviewState
      }
    }
  },
  blockClassName: getClassNameModern(donationBlockClasses, 'block', 'mb-6')
}

const donationFormWidget : (rootState: State, setRootState: (_: Updater<State>) => void, blockIndex: number) => StandardLocalWidget<DonationFormState> = (rootState, setRootState, blockIndex) => s =>
  fromJSX<Updater<DonationFormState>>(setState => {
    const w = any<Updater<DonationFormState>>([
      asyncLoader(s),
      async<{}>(s.submitState).map<Updater<DonationFormState>>(fieldUpdater<DonationFormState>()("submitState")),
      s.step == '1-Unloaded' ?
        fromJSX(() => <Loader type='inline' />)
        : s.step == '2-AmountForm' ?
          step2AmountFormWidget(s)
          : s.step == '3-PersonalForm' ?
            step3PersonalFormWidget(s)
            : nothing()
    ])
    // Visible step number: '1-Unloaded' (loading) and '2-AmountForm' are both
    // the initial step (step-1); '3-PersonalForm' is step-2, and so on.
    const stepNumber = Math.max(1, Number(s.step.split('-')[0]) - 1)
    const form = DonationBlockLayout.Form({
      html: w.run(setState),
      showHeader: s.step == '3-PersonalForm',
      title: s.title,
      step: stepNumber
    })
    const extras = DonationBlockLayout.Extras({
      expandables: <ExpandableLayout.Items
        items={s.expandableItems}
        rootState={rootState}
        setRootState={setRootState}
      />,
      text: s.text ? s.text : '',
      state: rootState
    })

    const loader = rootState.rootModernBlocksState.loader
    if (blockIndex == 0 && loader.kind == 'loaded') {
      const pageHeader = loader.value.pageData.header
      if (pageHeader.kind == 'normal' || pageHeader.kind == 'fullscreen') {
        return DonationBlockLayout.Wrapper(
          extras,
          <DonationHeaderLayout header={pageHeader} form={form} step={stepNumber} />
        )
      }
    }

    return DonationBlockLayout.Wrapper(<>{form}{extras}</>)
  })
const asyncLoader : StandardLocalWidget<DonationFormState> = s0 => {
  if(s0.step !== '1-Unloaded') return nothing()
  return async(s0.loader).map(u => s1 => {
    if(s1.step != '1-Unloaded') return s1

    const updatedLoader = u(s1.loader)
    if(updatedLoader.kind == 'loaded') {
      const step2 = initStep2(updatedLoader.value)(s1)

      const url = new URL(window.document.URL)
      const propositionAmount = Number(url.searchParams.get("donationAmount"))

      if (propositionAmount) return createCustomStep3(step2, propositionAmount)

      return step2
    }

    return {...s1, loader: updatedLoader}
  })
}
